import { useQueries, UseQueryOptions } from '@tanstack/react-query';
import { getVehicleScoreDay } from '../../services/VehiclesService';
import { VehicleScoreDay } from '../../types/Vehicles';

export interface FleetHourlyScore {
  scoreDate: string;
  score: number;
}

export function useFleetDayScores(
  fleetId: string,
  vehicleIds: string[],
  date: string,
): { scores: FleetHourlyScore[]; isLoading: boolean } {
  const queries: UseQueryOptions<VehicleScoreDay>[] = vehicleIds.map((id) => ({
    queryKey: ['vehicle-score-day', id, fleetId, date],
    queryFn: () => getVehicleScoreDay(id, fleetId, date),
    retry: 1,
    retryDelay: 3000,
    keepPreviousData: true,
    refetchInterval: false as const,
    enabled: !!id && !!date && !!fleetId,
  }));

  const results = useQueries({ queries });

  const isLoading = vehicleIds.length > 0 && date.length > 0 && results.some((r) => r.isLoading || r.isFetching);

  const allSnapshots = results.flatMap(
    (r) => (r.data?.hourlySnapshots ?? []).filter((s) => (s.drivingTimeMs ?? 0) > 0),
  );
  const hourMap = allSnapshots.reduce<Map<number, { scoreSum: number; count: number }>>((acc, snap) => {
    const entry = acc.get(snap.hour) ?? { scoreSum: 0, count: 0 };
    acc.set(snap.hour, { scoreSum: entry.scoreSum + snap.score, count: entry.count + 1 });
    return acc;
  }, new Map());

  const dayDate = date ? new Date(`${date}T00:00:00Z`) : null;
  const scores: FleetHourlyScore[] = dayDate
    ? Array.from(hourMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([hour, { scoreSum, count }]) => ({
        scoreDate: new Date(Date.UTC(dayDate.getUTCFullYear(), dayDate.getUTCMonth(), dayDate.getUTCDate(), hour)).toISOString(),
        score: Number((scoreSum / count).toFixed(4)),
      }))
    : [];

  return { scores, isLoading };
}
